/**
 * D50: this iPad isn't set up. The mechanic asks from here; the Manager approves in the office
 * app (matching the 4-digit code), and this screen moves on to the PIN screen by itself.
 */
import { useEffect, useState } from 'react';
import { api, errorText } from '../lib/api';
import { Big, bigInput } from './ui';

type Req = { id: number; status: 'PENDING' | 'APPROVED' | 'DENIED' | 'EXPIRED'; code: string; requesterName: string; deviceName: string };

export function RequestScreen() {
  const [req, setReq] = useState<Req | null>(null);
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // An earlier request from this iPad (the app was closed while waiting).
  useEffect(() => {
    api.get<Req>('/auth/device-request').then(setReq, () => undefined).finally(() => setChecked(true));
  }, []);

  // Wait for the Manager; approval makes this iPad's cookie a device, so just reload.
  useEffect(() => {
    if (req?.status !== 'PENDING') return;
    const t = setInterval(() => {
      api.get<Req>('/auth/device-request').then((r) => {
        if (r.status === 'APPROVED') window.location.reload();
        else setReq(r);
      }, () => undefined);
    }, 5000);
    return () => clearInterval(t);
  }, [req?.status]);

  const ask = async () => {
    setError(null);
    setBusy(true);
    try {
      setReq(await api.post<Req>('/auth/device-request', { requesterName: name, deviceName: deviceName || undefined }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (!checked) return <p className="p-8 text-xl">Loading…</p>;

  if (req?.status === 'PENDING') {
    return (
      <div className="mx-auto max-w-xl p-8 text-center text-lg">
        <h1 className="mb-2 text-3xl font-black">Waiting for approval</h1>
        <p className="mb-6 text-slate-600">The Manager will see this request in the office app. Tell them this code:</p>
        <p className="mb-6 font-mono text-7xl font-black tracking-widest">{req.code}</p>
        <p className="text-slate-600">{req.requesterName} · {req.deviceName}</p>
        <p className="mt-6 text-base text-slate-500">This screen moves on by itself once it's approved.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl p-8 text-lg">
      <h1 className="mb-2 text-3xl font-black">Set up this iPad</h1>
      {req?.status === 'DENIED' && <p className="mb-4 rounded-xl bg-red-100 p-4 text-red-900">The Manager turned down the last request. Check with them, then ask again.</p>}
      {req?.status === 'EXPIRED' && <p className="mb-4 rounded-xl bg-amber-100 p-4 text-amber-900">The last request wasn't answered within a day. Ask again.</p>}
      <p className="mb-6 text-slate-600">Ask the Manager to let this iPad sign mechanics in. You'll get a code to read to them.</p>
      <label className="mb-4 block">
        <span className="mb-1 block font-semibold">Your name</span>
        <input className={bigInput} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
      </label>
      <label className="mb-6 block">
        <span className="mb-1 block font-semibold">iPad name <span className="font-normal text-slate-500">(optional, e.g. Truck 3)</span></span>
        <input className={bigInput} value={deviceName} onChange={(e) => setDeviceName(e.target.value)} />
      </label>
      {error && <p className="mb-4 rounded-xl bg-red-100 p-4 text-red-900">{error}</p>}
      <Big tone="go" className="w-full" disabled={busy || name.trim().length < 2} onClick={ask}>Ask the Manager</Big>
      <p className="mt-8 text-base text-slate-500">
        Manager or Admin? You can also <a className="underline" href="/login?office=1">sign in</a> and enroll it under Devices.
      </p>
    </div>
  );
}
